/**
 * DeleteConfirmModal Component
 * 
 * Standardized delete confirmation modal.
 * Displays warning, entity info, and confirm/cancel buttons.
 */

import React from 'react';
import { X, AlertTriangle, Trash2, RefreshCw } from 'lucide-react';

export interface DeleteConfirmModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Confirm delete handler */
  onConfirm: () => void;
  /** Modal title (e.g., "Delete Certificate") */
  title: string;
  /** Entity type for message (e.g., "certificate") */
  entityType: string;
  /** Entity name to display */
  entityName: string;
  /** Additional info to display (e.g., domains, counts) */
  additionalInfo?: React.ReactNode;
  /** Whether deletion is in progress */
  isDeleting: boolean;
  /** Custom warning message */
  warningMessage?: string;
}

export function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  entityType,
  entityName,
  additionalInfo,
  isDeleting,
  warningMessage = 'This action cannot be undone.',
}: DeleteConfirmModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-red-600">{title}</h3>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="mb-6">
          <div className="flex items-center mb-3">
            <AlertTriangle className="w-6 h-6 text-red-500 mr-3 flex-shrink-0" />
            <p className="text-gray-900 font-medium">
              Are you sure you want to delete this {entityType}?
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-600 mb-1 capitalize">{entityType} Name:</p>
            <p className="font-medium text-gray-900">{entityName}</p>
            {additionalInfo}
          </div>
          
          <p className="text-sm text-red-600">
            <strong>{warningMessage}</strong> The {entityType} will be permanently removed from the system.
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isDeleting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Delete {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
